import { useState, useEffect, useRef, useCallback } from 'react';
import { formatBRL, parseBRLInput } from '@caixa/lib/format';
import { MoneyInput } from '@caixa/components/MoneyInput';
import type { ValoresPagamento } from '@caixa/types';

interface CashPaymentModalProps {
  open: boolean;
  total: number;
  onConfirm: (valores: ValoresPagamento, totalComDesconto?: number) => void | Promise<void>;
  onCancel: () => void;
}

export function CashPaymentModal({ open, total, onConfirm, onCancel }: CashPaymentModalProps) {
  const [recebido, setRecebido] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const recebidoNum = parseBRLInput(recebido);
  const troco = Math.max(0, recebidoNum - total);
  const valorInsuficiente = recebidoNum > 0 && recebidoNum < total;
  const canConfirm = recebidoNum >= total && !loading;

  useEffect(() => {
    if (open) {
      setRecebido('');
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    setLoading(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onCancel();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onCancel]);

  const handleConfirm = useCallback(async () => {
    if (!canConfirm) return;
    setLoading(true);
    const valores: ValoresPagamento = {
      pix: 0,
      dinheiro: total,
      credito: 0,
      debito: 0,
      dinheiroRecebido: recebidoNum,
      troco,
    };
    try {
      await onConfirm(valores, total);
    } catch {
      /* erro exibido pelo handler pai */
    } finally {
      setLoading(false);
    }
  }, [canConfirm, total, recebidoNum, troco, onConfirm]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Enter' && canConfirm) {
        e.preventDefault();
        void handleConfirm();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, canConfirm, handleConfirm]);

  if (!open) return null;

  return (
    <div
      className="caixa-overlay caixa-overlay--60"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cash-payment-title"
    >
      <div className="caixa-modal">
        <h2 id="cash-payment-title" className="sr-only">
          Pagamento em dinheiro
        </h2>
        <div className="caixa-field">
          <span className="caixa-label">Total</span>
          <div className="text-xl font-bold text-[#f5f5f5]">{formatBRL(total)}</div>
        </div>
        <div className="caixa-field">
          <label htmlFor="cash-recebido">Recebido</label>
          <MoneyInput
            ref={inputRef}
            id="cash-recebido"
            value={recebido}
            onChange={setRecebido}
            className="caixa-input text-lg"
          />
        </div>
        <div className="caixa-field">
          <span className="caixa-label">Troco</span>
          <div className={`text-xl font-bold ${troco > 0 ? 'text-emerald-400' : 'text-[#f5f5f5]'}`}>
            {formatBRL(troco)}
          </div>
        </div>
        {valorInsuficiente && (
          <p className="caixa-error">Valor insuficiente</p>
        )}
        <div className="caixa-modal-footer">
          <button
            type="button"
            onClick={onCancel}
            className="caixa-btn-outline"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!canConfirm}
            className="caixa-btn-success"
          >
            {loading ? 'Finalizando...' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
}
